'use server'

import { apiRequest, ApiResponse } from '../request'
import type { WalletLeadCategory } from './wallets-api'

export type DealStatus = 'OPEN' | 'WON' | 'LOST' | 'ABANDONED'

export interface DealItem {
    id: number
    dealId: number
    catalogId?: number
    name: string
    quantity: number
    unitPrice: number
    discount: number
    total: number
    notes?: string
    createdAt: string
    updatedAt: string
}

export interface StageTransition {
    id: number
    dealId: number
    fromStageId?: number
    fromStage?: {
        id: number
        name: string
    }
    toStageId: number
    toStage?: {
        id: number
        name: string
    }
    userId?: number
    user?: {
        id: number
        name: string
    }
    reason?: string
    duration?: number
    createdAt: string
}

export interface DealWinLossReason {
    id: number
    companyId: number
    name: string
    type: 'WIN' | 'LOSS'
    active: boolean
    createdAt: string
}

export interface Deal {
    id: number
    /** Só vem com busca: a oportunidade apareceu pelo que foi conversado, não pelo nome. */
    matchedInConversation?: boolean
    companyId: number
    title: string
    description?: string
    pipelineId: number
    pipeline?: {
        id: number
        name: string
    }
    stageId: number
    stage?: {
        id: number
        name: string
        color?: string
        probability: number
        sortOrder: number
    }
    leadId: number
    lead?: {
        id: number
        name: string
        phoneNumber: string
        email?: string
    }
    assignedUserId?: number
    assignedUser?: {
        id: number
        name: string
    }
    category: WalletLeadCategory
    status: DealStatus
    totalValue: number
    expectedCloseDate?: string
    closedAt?: string
    wonAt?: string
    lostAt?: string
    winLossReasonId?: number
    winLossReason?: DealWinLossReason
    winLossNotes?: string
    tags: string[]
    customFields?: any
    stageEnteredAt: string
    lastActivityAt?: string
    items?: DealItem[]
    transitions?: StageTransition[]
    consultations?: { id: number; title?: string; status: string; scheduledAt?: string; client?: { id: number; name: string } }[]
    exams?: { id: number; title?: string; status: string; scheduledAt?: string; client?: { id: number; name: string } }[]
    orders?: { id: number; status: string; scheduledAt?: string; catalog?: { id: number; name: string }; client?: { id: number; name: string } }[]
    teleconsultations?: { id: number; status: string; scheduledAt?: string; client?: { id: number; name: string } }[]
    billings?: { id: number; status: string; amount: number; paidAmount?: number; dueDate?: string; paidAt?: string; description?: string }[]
    deleted: boolean
    createdAt: string
    updatedAt: string
    _count?: {
        items: number
        transitions: number
    }
}

export interface CreateDealData {
    title: string
    description?: string
    pipelineId: number
    stageId: number
    leadId: number
    category: WalletLeadCategory
    assignedUserId?: number
    expectedCloseDate?: string
    tags?: string[]
    customFields?: any
    items?: CreateDealItemData[]
}

export interface UpdateDealData {
    title?: string
    description?: string
    stageId?: number
    assignedUserId?: number
    expectedCloseDate?: string
    tags?: string[]
    customFields?: any
}

export interface MoveDealStageData {
    stageId: number
    /** Funil de destino, para mover para OUTRO funil. Vazio = mesmo funil. */
    pipelineId?: number
    /**
     * Levar o cliente para a carteira da dona do funil de destino. Padrão: true.
     * Em `false`, a oportunidade e o lead trocam de dona e o cliente fica onde
     * está — a nova dona vê a conversa pelo funil da oportunidade.
     */
    transferWallet?: boolean
    reason?: string
}

export interface CloseDealData {
    status: 'WON' | 'LOST' | 'ABANDONED'
    winLossReasonId?: number
    winLossNotes?: string
}

export interface DealFilters {
    pipelineId?: number
    stageId?: number
    status?: DealStatus
    assignedUserId?: number
    startDate?: string
    endDate?: string
    /**
     * Busca livre: nome, telefone ou e-mail do contato, e título da oportunidade.
     * Ignora acentuação. O backend descarta termos com menos de 3 caracteres.
     */
    search?: string
}

export interface CreateDealItemData {
    catalogId?: number
    name: string
    quantity?: number
    unitPrice: number
    discount?: number
    notes?: string
}

// Deals

export interface DealsPage {
    items: Deal[]
    total: number
    page: number
    limit: number
}

export async function getDeals(
    filters?: DealFilters & { page?: number; limit?: number }
): Promise<ApiResponse<DealsPage>> {
    return apiRequest<DealsPage>('sales/deals', { params: filters })
}

export interface DealStageStats {
    count: number
    totalValue: number
}

export async function getDealCountsByStage(
    filters: { pipelineId: number } & Partial<DealFilters>
): Promise<ApiResponse<Record<number, DealStageStats>>> {
    return apiRequest<Record<number, DealStageStats>>('sales/deals/counts-by-stage', {
        params: filters,
    })
}

export async function getDeal(id: number): Promise<ApiResponse<Deal>> {
    return apiRequest<Deal>(`sales/deals/${id}`, {})
}

export async function createDeal(
    data: CreateDealData
): Promise<ApiResponse<Deal>> {
    return apiRequest<Deal>('sales/deals', { data }, 'post')
}

export async function updateDeal(
    id: number,
    data: UpdateDealData
): Promise<ApiResponse<Deal>> {
    return apiRequest<Deal>(`sales/deals/${id}`, { data }, 'patch')
}

export async function moveDealStage(
    id: number,
    data: MoveDealStageData
): Promise<ApiResponse<Deal>> {
    return apiRequest<Deal>(`sales/deals/${id}/move`, { data }, 'patch')
}

export async function closeDeal(
    id: number,
    data: CloseDealData
): Promise<ApiResponse<Deal>> {
    return apiRequest<Deal>(`sales/deals/${id}/close`, { data }, 'patch')
}

/** Oportunidades que entraram em cada funil depois da data informada: { pipelineId: count }. */
export async function getNewDealCounts(
    since: Record<number, string>
): Promise<ApiResponse<Record<number, number>>> {
    return apiRequest<Record<number, number>>('sales/deals/new-counts', { data: { since } }, 'post')
}

export async function deleteDeal(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`sales/deals/${id}`, {}, 'delete')
}

// Deal Items

export async function addDealItem(
    dealId: number,
    data: CreateDealItemData
): Promise<ApiResponse<Deal>> {
    return apiRequest<Deal>(
        `sales/deals/${dealId}/items`,
        { data },
        'post'
    )
}

export async function updateDealItem(
    dealId: number,
    itemId: number,
    data: Partial<CreateDealItemData>
): Promise<ApiResponse<Deal>> {
    return apiRequest<Deal>(
        `sales/deals/${dealId}/items/${itemId}`,
        { data },
        'patch'
    )
}

export async function removeDealItem(
    dealId: number,
    itemId: number
): Promise<ApiResponse<Deal>> {
    return apiRequest<Deal>(
        `sales/deals/${dealId}/items/${itemId}`,
        {},
        'delete'
    )
}

// Labels

export const DEAL_STATUS_LABELS: Record<DealStatus, string> = {
    OPEN: 'Aberto',
    WON: 'Ganho',
    LOST: 'Perdido',
    ABANDONED: 'Abandonado',
}

export const DEAL_STATUS_COLORS: Record<DealStatus, string> = {
    OPEN: 'badge-info',
    WON: 'badge-success',
    LOST: 'badge-error',
    ABANDONED: 'badge-ghost',
}
